import React, { useRef } from 'react';
import './TableGrid.css';
import GridLayout from 'react-grid-layout';
import type { Table, GridSize } from '../../types';
import { getTableProductStateClass, resolveTableColor } from '../../utils/tableState';
import { applyLayoutToTables, isValidTableLayout, revertTableLayout } from '../../utils/tableLayout';

// TableGrid renders the grid of tables and handles dragging, resizing and tap detection.
export interface TableGridProps {
  tables: Table[];
  gridSize: GridSize;
  mode: 'wall' | 'delete' | null;
  readOnly?: boolean;
  defaultTableColor: string;
  onTablesChange: (updated: Table[], description?: string) => void;
  onEditTable: (table: Table) => void;
  onDeleteTable: (table: Table) => void;
  isDraggingRef: React.MutableRefObject<boolean>;
  gridRef: React.RefObject<HTMLDivElement | null>;
  layoutHeight: number;
}

// Component for the grid of tables. Handles drag/resize and differentiates taps from drags on touch devices.
const TableGrid: React.FC<TableGridProps> = ({
  tables,
  gridSize,
  mode,
  readOnly = false,
  defaultTableColor,
  onTablesChange,
  onEditTable,
  onDeleteTable,
  isDraggingRef,
  gridRef,
  layoutHeight,
}) => {
  // Track the starting point of a touch to distinguish taps from drags
  const touchStart = useRef<{ x: number; y: number; time: number } | null>(null);

  // Mark the start of a drag or resize. Reset the dragging flag.
  function handleDragOrResizeStart(): void {
    isDraggingRef.current = false;
  }

  // Handle the end of a drag or resize. Update the tables and record history if moved/resized. Suppress modal opening after a drag.
  function handleDragOrResizeStop(
    layout: any,
    oldItem: any,
    newItem: any
  ): void {
    const moved =
      oldItem.x !== newItem.x ||
      oldItem.y !== newItem.y ||
      oldItem.w !== newItem.w ||
      oldItem.h !== newItem.h;
    if (readOnly) {
      return;
    }
    if (moved) {
      // Mark that a drag occurred. Suppress subsequent click until timeout.
      isDraggingRef.current = true;
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 250);
    } else {
      isDraggingRef.current = false;
    }
    if (!isValidTableLayout(newItem, tables, gridSize)) {
      // Revert invalid move by restoring old position
      onTablesChange(revertTableLayout(tables, oldItem.i, oldItem));
      return;
    }
    // Update tables to new position/size and record history
    const updated = applyLayoutToTables(tables, [newItem]);
    onTablesChange(updated, moved ? 'Moved/resized table' : undefined);
  }

  // Record the starting point of a touch to differentiate taps from drags.
  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    if (e.pointerType === 'touch') {
      touchStart.current = { x: e.clientX, y: e.clientY, time: Date.now() };
    } else {
      touchStart.current = null;
    }
  }

  // Handle pointer up on a table. If it qualifies as a tap, trigger deletion or editing based on the current mode.
  function handlePointerUp(
    e: React.PointerEvent<HTMLDivElement>,
    table: Table
  ): void {
    if (e.pointerType === 'touch' && touchStart.current) {
      const { x, y, time } = touchStart.current;
      const dx = e.clientX - x;
      const dy = e.clientY - y;
      const distance = Math.hypot(dx, dy);
      const duration = Date.now() - time;
      // Define a tap: quick touch (<300ms) and minimal movement (<10px)
      if (distance < 10 && duration < 300) {
        if (!isDraggingRef.current) {
          if (readOnly) {
            onEditTable(table);
          } else if (mode === 'delete') {
            onDeleteTable(table);
          } else {
            onEditTable(table);
          }
        }
      }
    }
    touchStart.current = null;
  }

  // Handler for mouse clicks on a table. Prevent immediate reopening after a drag by checking the dragging flag.
  function handleClick(table: Table): void {
    if (isDraggingRef.current) return;
    if (readOnly) {
      onEditTable(table);
    } else if (mode === 'delete') {
      onDeleteTable(table);
    } else {
      onEditTable(table);
    }
  }

  // Build the layout array for ReactGridLayout
  const layout = tables.map(t => ({ i: t.id, x: t.x, y: t.y, w: t.w, h: t.h, static: readOnly }));
  // Determine width based on grid container ref; fallback to 800px
  const width = gridRef.current && gridRef.current.clientWidth ? gridRef.current.clientWidth : 800;

  return (
    <GridLayout
      layout={layout}
      cols={gridSize.cols}
      rowHeight={50}
      width={width}
      onDragStart={handleDragOrResizeStart}
      onDragStop={handleDragOrResizeStop}
      onResizeStart={handleDragOrResizeStart}
      onResizeStop={handleDragOrResizeStop}
      preventCollision={true}
      compactType={null}
      isBounded={true}
      isDraggable={!readOnly}
      isResizable={!readOnly}
      style={{ height: layoutHeight }}
    >
      {tables.map(t => (
        <div
          key={t.id}
          style={{ background: resolveTableColor(t, defaultTableColor), borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: readOnly ? 'pointer' : mode === 'delete' ? 'not-allowed' : 'move' }}
          className={getTableProductStateClass(t)}
          onClick={() => handleClick(t)}
          onPointerDown={handlePointerDown}
          onPointerUp={e => handlePointerUp(e, t)}
        >
          {t.name}
        </div>
      ))}
    </GridLayout>
  );
};

export default TableGrid;
